import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Search, Download, Plus, Loader2, FileText, 
  Calendar, IndianRupee, Upload, Info, ArrowUpDown,
  ChevronDown, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnerLedgers = () => {
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [feePerWill, setFeePerWill] = useState(500);
  const [feePerRenewal, setFeePerRenewal] = useState(250);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isFeeModalOpen, setIsFeeModalOpen] = useState(false);
  const [outstandingDate, setOutstandingDate] = useState(new Date().toISOString().split('T')[0]);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    amount_paid: '',
    notes: ''
  });
  const [paymentAttachment, setPaymentAttachment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const adminToken = localStorage.getItem('adminToken');
  const axiosConfig = { headers: { Authorization: `Bearer ${adminToken}` } };

  useEffect(() => {
    fetchPartners();
  }, [includeDeleted]);

  useEffect(() => {
    if (selectedPartnerId) {
      fetchLedger(selectedPartnerId);
    }
  }, [selectedPartnerId]);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/admin/partners/list-for-dropdown?include_deleted=${includeDeleted}`, axiosConfig);
      setPartners(response.data.partners || []);
    } catch (error) {
      toast.error('Failed to fetch partners');
    }
  };

  const fetchLedger = async (partnerId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/partner/${partnerId}/ledger`, axiosConfig);
      setSelectedPartner(response.data.partner);
      setLedgerEntries(response.data.entries || []);
      setFeePerWill(response.data.fee_per_will || 500);
      setFeePerRenewal(response.data.fee_per_renewal || 250);
    } catch (error) {
      toast.error('Failed to fetch ledger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!selectedPartnerId) {
      toast.error('Please select a partner first');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('payment_date', paymentForm.payment_date);
      formData.append('reference_number', paymentForm.reference_number);
      formData.append('amount_paid', paymentForm.amount_paid);
      if (paymentForm.notes) formData.append('notes', paymentForm.notes);
      if (paymentAttachment) formData.append('attachment', paymentAttachment);

      await axios.post(
        `${API}/admin/partner/${selectedPartnerId}/payment`,
        formData,
        { ...axiosConfig, headers: { ...axiosConfig.headers, 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Payment added successfully');
      setIsPaymentModalOpen(false);
      setPaymentForm({ payment_date: new Date().toISOString().split('T')[0], reference_number: '', amount_paid: '', notes: '' });
      setPaymentAttachment(null);
      fetchLedger(selectedPartnerId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateFee = async () => {
    if (!selectedPartnerId) return;
    
    try {
      await axios.put(
        `${API}/admin/partner/${selectedPartnerId}/fee`,
        { fee_per_will: feePerWill, fee_per_renewal: feePerRenewal },
        axiosConfig
      );
      toast.success('Fees updated successfully');
      setIsFeeModalOpen(false);
    } catch (error) {
      toast.error('Failed to update fees');
    }
  };

  const handleDownloadOutstanding = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/outstanding-amounts?as_of_date=${outstandingDate}`,
        { ...axiosConfig, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Outstanding_Amounts_${outstandingDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedEntries = React.useMemo(() => {
    if (!sortConfig.key) return ledgerEntries;
    
    return [...ledgerEntries].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      if (sortConfig.key === 'created_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      return sortConfig.direction === 'asc' 
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [ledgerEntries, sortConfig]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    // Convert to IST
    const istOptions = { 
      timeZone: 'Asia/Kolkata',
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleString('en-IN', istOptions) + ' IST';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    const isNegative = amount < 0;
    const formatted = `₹${Math.abs(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return isNegative ? `-${formatted}` : formatted;
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'will_purchase': return 'Will Purchase';
      case 'will_renewal': return 'Will Renewal';
      case 'payment_from_willkart': return 'Payment from WillKart';
      default: return type;
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'will_purchase':
      case 'will_renewal':
        return 'text-emerald-400 bg-emerald-400/10';
      case 'payment_from_willkart':
        return 'text-blue-400 bg-blue-400/10';
      default:
        return 'text-slate-400 bg-slate-400/10';
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-ledgers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Partner Ledgers</h1>
          <p className="text-slate-400 mt-1">Manage partner accounts and payments</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Outstanding Amounts Download */}
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={outstandingDate}
              onChange={(e) => setOutstandingDate(e.target.value)}
              className="bg-slate-800/50 border-slate-700 text-white w-40"
            />
            <Button
              onClick={handleDownloadOutstanding}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Download className="w-4 h-4 mr-2" />
              Outstanding Amounts
            </Button>
          </div>
        </div>
      </div>

      {/* Partner Selection & Fee Configuration */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <Label className="text-slate-400 mb-2 block">Select Partner</Label>
              <Select value={selectedPartnerId} onValueChange={setSelectedPartnerId}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                  <SelectValue placeholder="Select a partner..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {partners.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="text-white hover:bg-slate-800">
                      {p.name} ({p.email}){p.status === 'Deleted' ? ' — Deleted' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include-deleted"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
                className="rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
                data-testid="include-deleted-toggle"
              />
              <Label htmlFor="include-deleted" className="text-slate-400 cursor-pointer text-sm">
                Include deleted partners
              </Label>
            </div>
            
            {selectedPartnerId && (
              <>
                <div className="flex items-center gap-2">
                  <div className="bg-slate-800 rounded-lg px-4 py-2 flex items-center gap-3">
                    <div>
                      <span className="text-slate-500 text-xs">Creation</span>
                      <span className="text-amber-400 font-bold ml-1">₹{feePerWill}</span>
                    </div>
                    <div className="w-px h-6 bg-slate-700" />
                    <div>
                      <span className="text-slate-500 text-xs">Renewal</span>
                      <span className="text-amber-400 font-bold ml-1">₹{feePerRenewal}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setIsFeeModalOpen(true)}
                    variant="outline"
                    size="sm"
                    className="border-slate-600 text-slate-300 hover:bg-slate-800"
                    data-testid="configure-fee-btn"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
                
                <Button
                  onClick={() => setIsPaymentModalOpen(true)}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Payment
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ledger Table */}
      {selectedPartnerId && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="border-b border-slate-800">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-400" />
                Ledger for {selectedPartner?.name}
              </CardTitle>
              <span className="text-slate-400 text-sm">{ledgerEntries.length} entries</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              </div>
            ) : ledgerEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="w-12 h-12 text-slate-600 mb-4" />
                <p className="text-slate-400">No ledger entries yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="ledger-table">
                  <thead>
                    <tr className="border-b border-slate-800 bg-slate-800/30">
                      <th 
                        className="text-left p-3 text-slate-400 font-medium text-sm cursor-pointer hover:text-white"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center gap-1">
                          Date/Time (IST)
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="text-left p-3 text-slate-400 font-medium text-sm">Client</th>
                      <th className="text-left p-3 text-slate-400 font-medium text-sm">Type</th>
                      <th 
                        className="text-right p-3 text-slate-400 font-medium text-sm cursor-pointer hover:text-white"
                        onClick={() => handleSort('amount')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Amount
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="text-right p-3 text-slate-400 font-medium text-sm">To WillKart</th>
                      <th className="text-right p-3 text-slate-400 font-medium text-sm">Net Amount</th>
                      <th 
                        className="text-right p-3 text-slate-400 font-medium text-sm cursor-pointer hover:text-white"
                        onClick={() => handleSort('cumulative_total')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          Cumulative
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="text-left p-3 text-slate-400 font-medium text-sm">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEntries.map((entry, idx) => (
                      <tr 
                        key={entry.id || idx}
                        className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                      >
                        <td className="p-3 text-slate-300 text-sm whitespace-nowrap">
                          {formatDateTime(entry.created_at)}
                        </td>
                        <td className="p-3 text-white text-sm">
                          {entry.client_name || '—'}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTransactionTypeColor(entry.transaction_type)}`}>
                            {getTransactionTypeLabel(entry.transaction_type)}
                          </span>
                        </td>
                        <td className={`p-3 text-right text-sm font-medium ${entry.amount >= 0 ? 'text-emerald-400' : 'text-blue-400'}`}>
                          {formatCurrency(entry.amount)}
                        </td>
                        <td className="p-3 text-right text-amber-400 text-sm">
                          {entry.amount_to_willkart > 0 ? formatCurrency(entry.amount_to_willkart) : '—'}
                        </td>
                        <td className={`p-3 text-right text-sm font-medium ${entry.net_amount >= 0 ? 'text-emerald-400' : 'text-blue-400'}`}>
                          {formatCurrency(entry.net_amount)}
                        </td>
                        <td className={`p-3 text-right text-sm font-bold ${entry.cumulative_total >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {formatCurrency(entry.cumulative_total)}
                        </td>
                        <td className="p-3 text-slate-400 text-sm">
                          {entry.reference_number || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Payment Modal */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-amber-400" />
              Add Payment Details
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Payment Date *</Label>
              <Input
                type="date"
                required
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                className="bg-slate-800/50 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Reference Number *</Label>
              <Input
                type="text"
                required
                placeholder="e.g., TXN123456"
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Amount Paid (₹) *</Label>
              <Input
                type="number"
                required
                min="0"
                step="0.01"
                placeholder="0.00"
                value={paymentForm.amount_paid}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, amount_paid: e.target.value }))}
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Notes (Optional)</Label>
              <textarea
                rows="2"
                placeholder="Any additional notes..."
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-md p-2 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Attachment (Optional)</Label>
              <div 
                className="border-2 border-dashed border-slate-700 rounded-lg p-4 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files[0];
                  if (file) setPaymentAttachment(file);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => setPaymentAttachment(e.target.files?.[0] || null)}
                />
                {paymentAttachment ? (
                  <div className="text-emerald-400">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">{paymentAttachment.name}</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-400 mt-2"
                      onClick={(e) => { e.stopPropagation(); setPaymentAttachment(null); }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="text-slate-400">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click or drag file to upload</p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPaymentModalOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Payment'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fee Configuration Modal */}
      <Dialog open={isFeeModalOpen} onOpenChange={setIsFeeModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-400" />
              Configure WillKart Fees
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Fee per Will Creation (₹)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={feePerWill}
                onChange={(e) => setFeePerWill(parseFloat(e.target.value) || 0)}
                className="bg-slate-800/50 border-slate-700 text-white"
                data-testid="fee-per-will-input"
              />
              <p className="text-slate-500 text-xs mt-1">
                Deducted when a client creates a new will
              </p>
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">Fee per Will Renewal (₹)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={feePerRenewal}
                onChange={(e) => setFeePerRenewal(parseFloat(e.target.value) || 0)}
                className="bg-slate-800/50 border-slate-700 text-white"
                data-testid="fee-per-renewal-input"
              />
              <p className="text-slate-500 text-xs mt-1">
                Deducted when a client renews their will access
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
              <p className="text-slate-400 text-sm">
                <span className="text-amber-400 font-medium">Example:</span> If client pays ₹1,999 for a new will and fee is ₹{feePerWill}, 
                partner receives ₹{(1999 - feePerWill).toLocaleString('en-IN')} net.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setIsFeeModalOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateFee}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="update-fees-btn"
              >
                Update Fees
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerLedgers;
